import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Friendly, full-screen "no access" state shown when a signed-in user without
 * the `admin` role opens the Admin Console at /admin.
 *
 * Replaces the old silent redirect to the owner dashboard — that bounce made
 * the console look broken instead of explaining that the account simply isn't
 * an administrator.
 */
export default function AdminAccessDenied() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  function handleSwitchAccount() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-background-200 bg-background-50 p-8 text-center">
        {/* Icon bubble */}
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-accent-100">
          <i className="ri-shield-cross-line text-3xl text-accent-700" />
        </span>

        {/* Heading */}
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          You don&apos;t have admin access
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
          The Admin Console is reserved for administrator accounts. Your account
          {user?.email ? (
            <>
              {" "}
              (<span className="font-medium text-foreground-800">{user.email}</span>)
            </>
          ) : null}{" "}
          doesn&apos;t have permission to view this area.
        </p>

        {/* What you can do */}
        <div className="mt-6 rounded-lg bg-background-100 px-4 py-3 text-left">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground-500">
            <i className="ri-information-line text-sm" /> What you can do
          </p>
          <ul className="mt-2 space-y-1.5 text-sm text-foreground-700">
            <li className="flex items-start gap-2">
              <i className="ri-check-line mt-0.5 shrink-0 text-accent-600" />
              Manage your listings from your owner dashboard
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-check-line mt-0.5 shrink-0 text-accent-600" />
              Sign in with an admin account if you should have access
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link
            to="/account"
            className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-dashboard-line text-base" />
            Go to my dashboard
          </Link>
          <button
            type="button"
            onClick={handleSwitchAccount}
            className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-background-300 bg-background-50 px-6 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-login-box-line text-base" />
            Switch account
          </button>
        </div>

        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-foreground-800"
        >
          <i className="ri-arrow-left-line text-sm" />
          Back to the directory
        </Link>
      </div>
    </div>
  );
}